
import {
  OwnTeamTableContractCell,
  OwnTeamTableAgeCell,
  OwnTeamTableCountryCell,
  OwnTeamTableEconomicCell,
  OwnTeamTableLocalityCell,
  OwnTeamTableRoleCell,
  AcademyTableTeamCell,
  AcademyTablePositionCell,
  AcademyTableRemovePlayerCell
} from '../tableCells';

import { JSX } from 'react';
import { PlayerId } from '../../../types/player';
import { StringToAnyMap } from '../../../types/common';
import { getCurrencyExtension } from '../../../utils/economyUtils';
import { translate } from '../../../../common/language/translations';
import { UserConfig } from '../../../types/user';


interface OwnTeamTableColumn {
  key: string;
  property: string;
  width: number;
  title?: string;
  isProtectedColumn?: boolean;
  isNotSortable?: boolean;
  isButtonCell?: boolean;
  paddingRight?: number;
  cell: (row: StringToAnyMap) => JSX.Element;
}


export const ownTeamTablePadding = 20;
export const ownTeamTableColumnsWidth = (
  userConfig: UserConfig | undefined | null,
  isAcademyTable?: boolean,
): number => {

  if (isAcademyTable) {
    return 710 + ownTeamTablePadding;
  }

  let width = 360;

  if (userConfig?.accessConfig.hasAccessToEconomy) {
    width += 445;
  }

  if (userConfig?.accessConfig.hasAccessToViewTeamRoles) {
    width += 90;
  }

  return width + ownTeamTablePadding;
};


export const getOwnTeamTableColumns = (
  isAcademyTable: boolean,
  removePlayerFromAcademy: (playerId: PlayerId) => void,
  tenantCountryCode: string,
  userConfig: UserConfig,
): OwnTeamTableColumn[] => {

  if (isAcademyTable) {
    return [
      {
        key: 'age',
        property: 'birth_date',
        width: 90,
        cell: (row) => (
          <OwnTeamTableAgeCell row={row} language={userConfig.language} />
        ),
      },
      {
        key: 'contract',
        property: 'contract_expiration',
        width: 90,
        cell: (row) => (
          <OwnTeamTableContractCell row={row} language={userConfig.language} />
        ),
      },
      {
        key: 'country',
        property: 'country_code',
        width: 90,
        cell: (row) => (
          <OwnTeamTableCountryCell row={row} language={userConfig.language} />
        ),
      },
      {
        key: 'team',
        property: 'team',
        width: 120,
        isNotSortable: true,
        cell: (row) => (
          <AcademyTableTeamCell row={row} />
        ),
      },
      {
        key: 'teamPosition',
        property: 'position',
        width: 90,
        isNotSortable: true,
        cell: (row) => (
          <AcademyTablePositionCell row={row} language={userConfig.language} />
        ),
      },
      {
        key: 'academyRole',
        property: 'academyRole',
        width: 120,
        cell: (row) => (
          <div className='table-cell'>
            <OwnTeamTableRoleCell row={row} tenant={userConfig.club} isAcademyTable={isAcademyTable} language={userConfig.language} />
          </div>
        ),
      },
      {
        key: 'removePlayer',
        property: '-',
        isNotSortable: true,
        isButtonCell: true,
        width: 110 + ownTeamTablePadding,
        paddingRight: ownTeamTablePadding,
        cell: (row) => (
          <div className='table-cell' style={{ paddingRight: ownTeamTablePadding }}>
            <AcademyTableRemovePlayerCell row={row} removePlayerFromAcademy={removePlayerFromAcademy} language={userConfig.language} />
          </div>
        ),
      }
    ];
  }

  const localityColumnPaddingRight = !userConfig?.accessConfig.hasAccessToEconomy && !userConfig?.accessConfig.hasAccessToViewTeamRoles
    ? ownTeamTablePadding
    : 0;

  const bonusesColumnPaddingRight = !userConfig?.accessConfig.hasAccessToViewTeamRoles
    ? ownTeamTablePadding
    : 0;

  const ownTeamColumns: OwnTeamTableColumn[] = [
    {
      key: 'age',
      property: 'birth_date',
      width: 90,
      cell: (row) => (
        <OwnTeamTableAgeCell row={row} language={userConfig.language} />
      ),
    },
    {
      key: 'contract',
      property: 'contract_expiration',
      width: 90,
      cell: (row) => (
        <OwnTeamTableContractCell row={row} language={userConfig.language} />
      ),
    },
    {
      key: 'country',
      property: 'country_code',
      width: 85,
      cell: (row) => (
        <OwnTeamTableCountryCell row={row} language={userConfig.language} />
      ),
    },
    {
      key: 'locality',
      property: 'locality',
      width: 95 + localityColumnPaddingRight,
      paddingRight: localityColumnPaddingRight,
      cell: (row) => (
        <div className='table-cell' style={{ paddingRight: localityColumnPaddingRight }}>
          <OwnTeamTableLocalityCell row={row} language={userConfig.language} />
        </div>
      ),
    },
  ];

  if (userConfig?.accessConfig.hasAccessToEconomy) {
    ownTeamColumns.push(
      {
        key: 'marketValue',
        property: 'market_value',
        width: 120,
        title: getCurrencyExtension(tenantCountryCode),
        isProtectedColumn: true,
        cell: (row) => (
          <OwnTeamTableEconomicCell row={row} property={'market_value'} />
        ),
      },
      {
        key: 'signingCost',
        property: 'signingCost',
        width: 120,
        title: translate('signingCostLong', userConfig.language) + ' ' + getCurrencyExtension(tenantCountryCode),
        isProtectedColumn: true,
        cell: (row) => (
          <OwnTeamTableEconomicCell row={row} property={'signingCost'} />
        ),
      },
      {
        key: 'salary',
        property: 'salary',
        width: 105,
        title: translate('salaryCost', userConfig.language) + ' ' + getCurrencyExtension(tenantCountryCode),
        isProtectedColumn: true,
        cell: (row) => (
          <OwnTeamTableEconomicCell row={row} property={'salary'} />
        ),
      },
      {
        key: 'bonuses',
        property: 'bonuses',
        width: 100 + bonusesColumnPaddingRight,
        title: translate('bonusesExpected', userConfig.language) + ' ' + getCurrencyExtension(tenantCountryCode),
        isProtectedColumn: true,
        paddingRight: bonusesColumnPaddingRight,
        cell: (row) => (
          <div className='table-cell' style={{ paddingRight: bonusesColumnPaddingRight }}>
            <OwnTeamTableEconomicCell row={row} property={'bonuses'} />
          </div>
        ),
      },
    );
  }

  if (userConfig?.accessConfig.hasAccessToViewTeamRoles) {
    ownTeamColumns.push(
      {
        key: 'teamRole',
        property: 'role',
        width: 90 + ownTeamTablePadding,
        isProtectedColumn: true,
        paddingRight: ownTeamTablePadding,
        cell: (row) => (
          <div className='table-cell' style={{ paddingRight: ownTeamTablePadding }}>
            <OwnTeamTableRoleCell row={row} tenant={userConfig.club} isAcademyTable={isAcademyTable} language={userConfig.language} />
          </div>
        ),
      },
    );
  }

  return ownTeamColumns;
};
